import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
 ColumnProps,
 IFormField,
 ISorting,
} from 'models/Common'
import {
 formatDateForDisplay,
 formatNumberForDisplay,
 formatString,
} from 'utils/helper'
import {IOrder} from 'models/OrderManagement'
import KTTable from 'helpers/ui-components/KTTable'
import {
 getDisplayNegotiationStatus,
 getNegotiationStatusColorClass,
 isDistributorUser,
 isNegotiatedOrder,
} from './tradeNegotiationUtils'

type IProps = {
 activeTabId: string
 loggedInUserId: number | undefined
 totalRecords: number
 currentPage: number
 orderList: IOrder[]
 sortConfig: ISorting
 handleActionBtnClick: (newState: IFormField | null) => void
 setAggregatorId: (id: number | null) => void
 setSelectedOrder: (newState: IFormField | null) => void
 handlePageChange: (pageNo: number) => void
 setLoader: (newState: boolean) => void
 setSortConfig: (newState: ISorting) => void
 itemsPerPage: number
 setItemsPerPage: (itemsPerPage: number) => void
}

const OrderTable = (props: IProps) => {
 const {t} = useTranslation()
 const userAccess = localStorage.getItem('user')
 const userCategoryKey = userAccess
  ? JSON.parse(userAccess)?.userCategoryKey
  : null
 const isDistributor = isDistributorUser(userCategoryKey)
 const [tableColList, setTableColList] = useState<
  Array<ColumnProps<IOrder>>
 >([])

 const renderOrderValueCell = (record: IOrder) => (
  <div>
   <div>{formatNumberForDisplay(record?.order_value)}</div>
   {isDistributor && isNegotiatedOrder(record) && (
    <span className='inline-flex items-center gap-1 text-[#CC293F] text-xs font-medium mt-1 px-2 py-0.5 bg-[#FFEBED] rounded-full'>
     <span>•</span>
     {t('orderManagement.negotiated')}
    </span>
   )}
  </div>
 )

 const newOrderTableConfig = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IOrder>> = [
   {
    key: 'srNo',
    title: t('orderManagement.tableCol.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'order_id',
    title: t('orderManagement.tableCol.orderId'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setSelectedOrder({
         orderId: record.order_id,
         actionsAllowed: record?.actions_allowed,
         isNegotiated: record?.is_negotiated,
         negotiationStatus: record?.negotiation_status,
        })
       }}
      >
       {record.display_order_number}
      </div>
     )
    },
   },
   {
    key: 'order_date',
    sort: true,
    title: t('orderManagement.tableCol.orderDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.order_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'customer_name',
    sort: true,
    className: 'w-[120px] ',
    title: t('orderManagement.tableCol.buyerName'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setAggregatorId(record.customer_id)
       }}
      >
       {record.customer_name}
      </div>
     )
    },
   },
   {
    key: 'order_value',
    sort: true,
    title: t('orderManagement.tableCol.orderValue'),
    className: 'w-[120px] ',
    render: (_, record) => renderOrderValueCell(record),
   },
   ...(isDistributor
    ? [
       {
        key: 'derived_negotiation_status',
        title: t('orderManagement.tableCol.status'),
        className: 'w-[140px]',
        render: (_: unknown, record: IOrder) => {
         const status = getDisplayNegotiationStatus(record)
         if (!status) {
          return <span className='text-[#80868C]'>—</span>
         }
         return (
          <p
           className={`${getNegotiationStatusColorClass(
            record
           )} font-medium text-sm`}
          >
           {status}
          </p>
         )
        },
       } as ColumnProps<IOrder>,
      ]
    : []),
   {
    key: 'action',
    title: t('orderManagement.tableCol.action'),
    className: 'w-[160px] text-center',
    render: (_, record) => {
     const isAcceptDisabled =
      !record?.actions_allowed ||
      (isDistributor &&
       isNegotiatedOrder(record) &&
       record?.negotiation_status ===
        'PENDING_BUYER_REVIEW')

     if (isDistributor) {
      return (
       <div className='flex items-center justify-center gap-5 w-full'>
        <button
         type='button'
         disabled={isAcceptDisabled}
         className={`
          text-sm font-semibold px-3 py-1 rounded-md transition-all
          ${
           isAcceptDisabled
            ? 'text-[#C5CED6] bg-[#F5F7F9] cursor-not-allowed'
            : 'text-[#0E7A41] hover:underline'
          }
        `}
         onClick={() => {
          props.handleActionBtnClick({
           orderId: record.order_id,
           status: 'CONFIRMED',
          })
         }}
        >
         {t('orderManagement.tableCol.accept')}
        </button>
        <button
         type='button'
         disabled={!record?.actions_allowed}
         className='text-[#CC293F] text-sm font-semibold bg-transparent border-0 p-0 disabled:text-[#C5CED6] disabled:cursor-not-allowed hover:underline'
         onClick={() => {
          props.handleActionBtnClick({
           orderId: record.order_id,
           status: 'REJECTED',
          })
         }}
        >
         {t('orderManagement.tableCol.reject')}
        </button>
       </div>
      )
     }

     return (
      <div className='flex items-center justify-center w-full'>
       <button
        type='button'
        disabled={!record?.actions_allowed}
        color='red'
        className='text-center py-[7px] px-4 bg-[#FFEBED] text-[#CC293F] !rounded-lg text-sm  
       hover:bg-[#CC293F] hover:text-white font-semibold border border-[#CC293F] mr-2'
        onClick={() => {
         props.handleActionBtnClick({
          orderId: record.order_id,
          status: 'REJECTED',
         })
        }}
       >
        {t('orderManagement.tableCol.reject')}
       </button>
       <button
        type='button'
        disabled={!record?.actions_allowed}
        color='green'
        className='text-center py-[7px] px-4 bg-[#DAF5E6] text-[#0E7A41] !rounded-lg text-sm  
       hover:bg-[#0E7A41] hover:text-white font-semibold border border-[#0E7A41]'
        onClick={() => {
         props.handleActionBtnClick({
          orderId: record.order_id,
          status: 'CONFIRMED',
         })
        }}
       >
        {t('orderManagement.tableCol.accept')}
       </button>
      </div>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }

 const dispatchTableConfig = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IOrder>> = [
   {
    key: 'srNo',
    title: t('orderManagement.tableCol.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'order_id',
    title: t('orderManagement.tableCol.orderId'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setSelectedOrder({
         orderId: record.order_id,
         actionsAllowed: record?.actions_allowed,
         isNegotiated: record?.is_negotiated,
         negotiationStatus: record?.negotiation_status,
        })
       }}
      >
       {record.display_order_number}
      </div>
     )
    },
   },
   {
    key: 'order_date',
    sort: true,
    title: t('orderManagement.tableCol.orderDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.order_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'customer_name',
    sort: true,
    className: 'w-[120px] ',
    title: t('orderManagement.tableCol.buyerName'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setAggregatorId(record.customer_id)
       }}
      >
       {record.customer_name}
      </div>
     )
    },
   },
   {
    key: 'delivery_date',
    title: t('orderManagement.tableCol.deliveryDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.delivery_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'term',
    title: t('orderManagement.tableCol.term'),
    className: 'w-[120px] ',
   },
   {
    key: 'total_paid_amount',
    title: t('orderManagement.tableCol.paymentDetailsPaid'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatNumberForDisplay(
      record?.total_paid_amount
     )
    },
   },
   {
    key: 'outstanding',
    title: t('orderManagement.tableCol.outstanding'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatNumberForDisplay(
      record?.order_value - record?.total_paid_amount
     )
    },
   },
   {
    key: 'order_value',
    sort: true,
    title: t('orderManagement.tableCol.orderValue'),
    className: 'w-[120px] ',
    render: (_, record) => renderOrderValueCell(record),
   },
   {
    key: 'action',
    title: t('orderManagement.tableCol.action'),
    className: 'w-[220px] text-center',
    render: (_, record) => {
     return (
      <div className='flex items-center justify-center w-full'>
       <button
        type='button'
        disabled={!record?.actions_allowed}
        color='red'
        className='text-center py-[7px] px-4 bg-[#FFEBED] text-[#CC293F] !rounded-lg text-sm  
       hover:bg-[#CC293F] hover:text-white font-semibold border border-[#CC293F] mr-2'
        onClick={() => {
         props.handleActionBtnClick({
          orderId: record.order_id,
          status: 'CANCELLED',
         })
        }}
       >
        {t('orderManagement.tableCol.cancel')}
       </button>
       <button
        type='button'
        disabled={!record?.actions_allowed}
        color='green'
        className='text-center py-[7px] px-2 bg-[#DAF5E6] text-[#0E7A41] !rounded-lg text-sm  
       hover:bg-[#0E7A41] hover:text-white font-semibold border border-[#0E7A41]'
        onClick={() => {
         props.handleActionBtnClick({
          orderId: record.order_id,
          status: 'SHIPPED',
         })
        }}
       >
        {t('orderManagement.tableCol.markDispatch')}
       </button>
      </div>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }

 const fulfilmentTableConfig = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IOrder>> = [
   {
    key: 'srNo',
    title: t('orderManagement.tableCol.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'order_id',
    title: t('orderManagement.tableCol.orderId'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setSelectedOrder({
         orderId: record.order_id,
         actionsAllowed: record?.actions_allowed,
         isNegotiated: record?.is_negotiated,
         negotiationStatus: record?.negotiation_status,
        })
       }}
      >
       {record.display_order_number}
      </div>
     )
    },
   },
   {
    key: 'order_date',
    sort: true,
    title: t('orderManagement.tableCol.orderDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.order_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'customer_name',
    sort: true,
    className: 'w-[120px] ',
    title: t('orderManagement.tableCol.buyerName'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setAggregatorId(record.customer_id)
       }}
      >
       {record.customer_name}
      </div>
     )
    },
   },
   {
    key: 'dispatch_date',
    title: t('orderManagement.tableCol.dispatchDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.dispatch_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'term',
    title: t('orderManagement.tableCol.term'),
    className: 'w-[120px] ',
   },
   {
    key: 'total_paid_amount',
    title: t('orderManagement.tableCol.paymentDetailsPaid'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatNumberForDisplay(
      record?.total_paid_amount
     )
    },
   },
   {
    key: 'outstanding',
    title: t('orderManagement.tableCol.outstanding'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatNumberForDisplay(
      record?.order_value - record?.total_paid_amount
     )
    },
   },
   {
    key: 'order_value',
    sort: true,
    title: t('orderManagement.tableCol.orderValue'),
    className: 'w-[120px] ',
    render: (_, record) => renderOrderValueCell(record),
   },
   {
    key: 'action',
    title: t('orderManagement.tableCol.action'),
    className: 'w-[150px] text-center',
    render: (_, record) => {
     return (
      <div className='flex items-center justify-center w-full'>
       {record.order_status === 'DELIVERED' ? (
        <button
         type='button'
         disabled={true}
         color='blue'
         className={`text-center !py-1 !px-2 !w-[92px] !bg-[#F1FFEB] rounded-sm text-xs !border-[#43A314] text-[#43A314] font-semibold 
            disabled:text-[#43A314]`}
        >
         {t('orderManagement.delivered')}
        </button>
       ) : (
        <button
         type='button'
         disabled={!record?.actions_allowed}
         color='green'
         className='text-center py-[7px] px-4 bg-[#DAF5E6] text-[#0E7A41] !rounded-lg text-sm  
        hover:bg-[#0E7A41] hover:text-white font-semibold border border-[#0E7A41]'
         onClick={() => {
          props.handleActionBtnClick({
           retailer_name: record.customer_name,
           orderId: record.order_id,
           status: 'DELIVERED',
          })
         }}
        >
         {t('orderManagement.markDelivery')}
        </button>
       )}
      </div>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }

 const paymentConfirmationTableConfig = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IOrder>> = [
   {
    key: 'srNo',
    title: t('orderManagement.tableCol.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'order_id',
    title: t('orderManagement.tableCol.orderId'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setSelectedOrder({
         orderId: record.order_id,
         actionsAllowed: record?.actions_allowed,
         isNegotiated: record?.is_negotiated,
         negotiationStatus: record?.negotiation_status,
        })
       }}
      >
       {record.display_order_number}
      </div>
     )
    },
   },
   {
    key: 'order_date',
    sort: true,
    title: t('orderManagement.tableCol.orderDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.order_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'customer_name',
    sort: true,
    className: 'w-[120px] ',
    title: t('orderManagement.tableCol.buyerName'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setAggregatorId(record.customer_id)
       }}
      >
       {record.customer_name}
      </div>
     )
    },
   },
   {
    key: 'delivery_date',
    title: t('orderManagement.tableCol.deliveryDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.delivery_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'term',
    title: t('orderManagement.tableCol.term'),
    className: 'w-[120px] ',
   },
   {
    key: 'total_paid_amount',
    title: t('orderManagement.tableCol.paymentDetailsPaid'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatNumberForDisplay(
      record?.total_paid_amount
     )
    },
   },
   {
    key: 'outstanding',
    title: t('orderManagement.tableCol.outstanding'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatNumberForDisplay(
      record?.order_value - record?.total_paid_amount
     )
    },
   },
   {
    key: 'order_value',
    sort: true,
    title: t('orderManagement.tableCol.orderValue'),
    className: 'w-[120px] ',
    render: (_, record) => renderOrderValueCell(record),
   },
   {
    key: 'action',
    title: t('orderManagement.tableCol.action'),
    className: 'w-[180px] text-center',
    render: (_, record) => {
     return (
      <div className='flex items-center justify-center w-full'>
       <button
        type='button'
        disabled={!record?.actions_allowed}
        color='green'
        className='text-center py-[7px] px-4 bg-[#DAF5E6] text-[#0E7A41] !rounded-lg text-sm  
       hover:bg-[#0E7A41] hover:text-white font-semibold border border-[#0E7A41]'
        onClick={() => {
         props.handleActionBtnClick({
          orderId: record.order_id,
          status: 'PAYMENT_CONFIRMED',
         })
        }}
       >
        {t('orderManagement.confirm')}
       </button>
       {/* <button
        type='button'
        disabled={!record?.actions_allowed}
        color='blue'
        className={`ml-2 text-center !bg-transparent text-sm text-[#4E83E4] font-medium underline underline-offset-auto 
                disabled:underline-0 disabled:text-[#C5CED6]`}
        onClick={() => {
         props.handleActionBtnClick({
          orderId: record.order_id,
          status: 'PAYMENT_CONFIRMED',
         })
        }}
       >
        {t('orderManagement.request')}
       </button> */}
      </div>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }

 const completedTableConfig = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IOrder>> = [
   {
    key: 'srNo',
    title: t('orderManagement.tableCol.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'order_id',
    title: t('orderManagement.tableCol.orderId'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setSelectedOrder({
         orderId: record.order_id,
         actionsAllowed: record?.actions_allowed,
         isNegotiated: record?.is_negotiated,
         negotiationStatus: record?.negotiation_status,
        })
       }}
      >
       {record.display_order_number}
      </div>
     )
    },
   },
   {
    key: 'order_date',
    sort: true,
    title: t('orderManagement.tableCol.orderDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.order_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'customer_name',
    sort: true,
    className: 'w-[120px] ',
    title: t('orderManagement.tableCol.buyerName'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setAggregatorId(record.customer_id)
       }}
      >
       {record.customer_name}
      </div>
     )
    },
   },
   {
    key: 'delivery_date',
    title: t('orderManagement.tableCol.deliveryDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.delivery_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'term',
    title: t('orderManagement.tableCol.term'),
    className: 'w-[120px] ',
   },
   {
    key: 'order_value',
    sort: true,
    title: t('orderManagement.tableCol.orderValue'),
    className: 'w-[120px] ',
    render: (_, record) => renderOrderValueCell(record),
   },
   {
    key: 'total_paid_amount',
    title: t('orderManagement.tableCol.paymentDetailsPaid'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatNumberForDisplay(
      record?.total_paid_amount
     )
    },
   },
   {
    key: 'order_status',
    title: t('orderManagement.tableCol.status'),
    className: 'w-[180px] text-center',
    render: (_, record) => {
     return (
      <p className='text-[#0E7A41] font-semibold text-xs'>
       {record.order_status}
      </p>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }

 const rejectedTableConfig = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IOrder>> = [
   {
    key: 'srNo',
    title: t('orderManagement.tableCol.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'order_id',
    title: t('orderManagement.tableCol.orderId'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setSelectedOrder({
         orderId: record.order_id,
         actionsAllowed: record?.actions_allowed,
         isNegotiated: record?.is_negotiated,
         negotiationStatus: record?.negotiation_status,
        })
       }}
      >
       {record.display_order_number}
      </div>
     )
    },
   },
   {
    key: 'order_date',
    sort: true,
    title: t('orderManagement.tableCol.orderDate'),
    className: 'w-[120px] ',
    render: (_, record) => {
     return formatDateForDisplay(
      record?.order_date,
      'DD-MMM-YYYY'
     )
    },
   },
   {
    key: 'customer_name',
    sort: true,
    className: 'w-[120px] ',
    title: t('orderManagement.tableCol.buyerName'),
    render: (_, record) => {
     return (
      <div
       className='w-full text-[#4E83E4] underline cursor-pointer'
       onClick={() => {
        props.setAggregatorId(record.customer_id)
       }}
      >
       {record.customer_name}
      </div>
     )
    },
   },
   {
    key: 'order_value',
    sort: true,
    title: t('orderManagement.tableCol.orderValue'),
    className: 'w-[120px] ',
    render: (_, record) => renderOrderValueCell(record),
   },
   {
    key: 'updated_by',
    title: t('orderManagement.tableCol.cancelledBy'),
   },
   {
    key: 'order_status',
    title: t('orderManagement.tableCol.status'),
    render: (_, record) => {
     return (
      <p className='text-[#CC293F]'>
       {formatString(record.order_status)}
      </p>
     )
    },
   },
   {
    key: 'reason',
    title: t('orderManagement.tableCol.reason'),
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }

 useEffect(() => {
  if (props.activeTabId === 'open_orders') {
   void newOrderTableConfig()
  } else if (props.activeTabId === 'accepted_orders') {
   void dispatchTableConfig()
  } else if (props.activeTabId === 'dispatched_orders') {
   void fulfilmentTableConfig()
  } else if (
   props.activeTabId === 'pending_payment_orders'
  ) {
   void paymentConfirmationTableConfig()
  } else if (props.activeTabId === 'completed_orders') {
   void completedTableConfig()
  } else if (
   props.activeTabId === 'rejected_cancelled_orders'
  ) {
   void rejectedTableConfig()
  } else {
   setTableColList([])
  }
 }, [props.orderList, props.activeTabId])

 return (
  <div className='w-full'>
   <KTTable
    data={props.orderList}
    columns={tableColList}
    headerClass='bg-[#80868C]'
    headerColClass='!border-0 text-left'
    colClass='text-[#33393D] !text-sm font-normal !border-0 !py-3'
    tableClass=''
    rowClass='even:bg-[#F5F8FA]'
    showPagination={true}
    currentPage={props.currentPage}
    itemsPerPage={props.itemsPerPage}
    totalRecord={props.totalRecords}
    onPageChange={props.handlePageChange}
    setSortConfig={props.setSortConfig}
    sortConfig={props.sortConfig}
    setItemsPerPage={props.setItemsPerPage}
   />
  </div>
 )
}

export default OrderTable
